import { useMemo } from "react";
import {
    ASSIGNABLE_WORKSPACE_ROLES,
    UserWorkspaceRole,
    userWorkspaceRolesInOrder,
} from "@common/userWorkspaceRoles";
import { AssignableWorkspaceRole } from "./lib";

function isAssignableDisplayRole(
    role: UserWorkspaceRole,
): role is AssignableWorkspaceRole {
    return role !== UserWorkspaceRole.PRIMARY_ADMIN;
}

/**
 * The picker's options come from `ASSIGNABLE_WORKSPACE_ROLES` — the same list the
 * invite and change-role contracts validate against — rather than a copy kept
 * here. That is what makes dropping a role from that list a real kill switch:
 * otherwise it would close the API while leaving the option on screen for an
 * admin to pick.
 *
 * The list is then narrowed by the workspace role hierarchy: you may only assign
 * roles at or below your own. `PRIMARY_ADMIN` never appears — it is absent from
 * the assignable list, and workspace ownership moves through its own transfer
 * endpoint.
 */
export function useAssignableWorkspaceLevelRoleOptions(
    actingUserRole: UserWorkspaceRole | null,
): AssignableWorkspaceRole[] {
    return useMemo(() => {
        if (!actingUserRole) {
            return [];
        }

        const selectableRoles = new Set(
            userWorkspaceRolesInOrder.slice(
                userWorkspaceRolesInOrder.indexOf(actingUserRole),
            ),
        );

        return ASSIGNABLE_WORKSPACE_ROLES.filter(
            (role): role is AssignableWorkspaceRole =>
                selectableRoles.has(role) && isAssignableDisplayRole(role),
        );
    }, [actingUserRole]);
}
